// src/services/assetDetailsService.js

import { repo } from "../dbservice/repo";

// ─────────────────────────────────────────────
// Error Class
// ─────────────────────────────────────────────
export class AssetDetailsError extends Error {
  constructor(code, message, cause = null) {
    super(message);
    this.name = "AssetDetailsError";
    this.code = code;
    this.cause = cause;
  }
}

// ─────────────────────────────────────────────
// Service Class
// Class name = AssetDetailsServiceClass  (different from the exported
// singleton const below → fixes "already declared" error)
// ─────────────────────────────────────────────
class AssetDetailsServiceClass {
  constructor() {
    this._collectionName = "assets";
  }

  async getAssetById(assetId) {
    if (!assetId || typeof assetId !== "string" || assetId.trim() === "") {
      throw new AssetDetailsError(
        "INVALID_ASSET_ID",
        "A valid asset ID must be provided.",
      );
    }

    try {
      const rawAsset = await repo.fetchById(this._collectionName, assetId);

      if (!rawAsset) {
        throw new AssetDetailsError(
          "ASSET_NOT_FOUND",
          `No asset found with ID: ${assetId}`,
        );
      }

      return this._formatAsset(rawAsset, assetId);
    } catch (err) {
      if (err instanceof AssetDetailsError) throw err;
      throw new AssetDetailsError(
        "FETCH_FAILED",
        "Failed to fetch asset details. Please try again.",
        err,
      );
    }
  }

  async getRelatedAssets(category, excludeId, limitVal = 5) {
    if (!category) return [];

    try {
      const filters   = [{ field: "category", value: category }];
      const sortRules = [{ field: "updatedAt", order: "desc" }];

      const results = await repo.fetchData(
        this._collectionName,
        filters,
        sortRules,
        limitVal + 1,
      );

      return results
        .filter((asset) => asset.id !== excludeId)
        .slice(0, limitVal)
        .map((asset) => this._formatAsset(asset, asset.id));
    } catch (err) {
      console.warn("[assetDetailsService] Related assets fetch failed:", err);
      return [];
    }
  }

  subscribeToAsset(assetId, onUpdate) {
    if (!assetId || typeof onUpdate !== "function") {
      console.error(
        "[assetDetailsService] subscribeToAsset: invalid arguments.",
      );
      return () => {};
    }

    const filters = [{ field: "id", value: assetId }];

    const handleUpdate = (assets) => {
      if (!assets || assets.length === 0) {
        onUpdate(null);
        return;
      }
      onUpdate(this._formatAsset(assets[0], assetId));
    };

    return repo.subscribeToUpdates(
      this._collectionName,
      filters,
      [],
      1,
      handleUpdate,
    );
  }

  _formatAsset(raw, fallbackId) {
    const source =
      raw.details && typeof raw.details === "object"
        ? { ...raw.details, id: raw.id || fallbackId }
        : raw;

    return {
      // Identity
      id:               source.id               || fallbackId,
      assetId:          source.assetId           || source.id  || "N/A",
      assetName:        source.assetName         || source.name || "N/A",
      // Classification
      category:         source.category          || "N/A",
      assetType:        source.assetType         || "N/A",
      assetDescription: source.assetDescription  || "N/A",
      qrCodeNo:         source.qrCodeNo          || "N/A",
      // Company & Location
      company:          source.company           || "N/A",
      companyId:        source.companyId         || "N/A",
      location:         source.location          || "N/A",
      locationCode:     source.locationCode      || "N/A",
      // Purchase Details
      vendor:           source.vendor            || "N/A",
      cost:             typeof source.cost === "number" ? source.cost : null,
      invoiceNumber:    source.invoiceNumber     || "N/A",
      warranty:         this._resolveDate(source.warranty),
      // Assignment & Status
      status:           source.status            || "Unknown",
      assignedTo:       source.assignedTo        || null,
      // Timestamps
      createdAt:        this._resolveDate(source.createdAt),
      updatedAt:        this._resolveDate(source.updatedAt || source.updated_at),
    };
  }

  _resolveDate(value) {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }
}

// ─────────────────────────────────────────────
// Singleton export
// const name ≠ class name → no redeclaration conflict
// ─────────────────────────────────────────────
const assetDetailsService = new AssetDetailsServiceClass();
export { assetDetailsService };